/**
 * NewBookingModal — dispatcher phone-booking form.
 *
 * Minimal launch-ready phone dispatch: the operator takes a call from a
 * (possibly unregistered) customer and books a ride on their behalf. The
 * backend finds-or-creates the customer by phone number and dispatches the
 * ride immediately through the normal flow. No CRM / invoicing here.
 */
import React, { useState, useMemo } from "react";
import { X, Phone, User, Loader2, MapPin, Clock, CalendarClock, Sparkles } from "lucide-react";
import { PlaceAutocomplete } from "../../components/PlaceAutocomplete";
import { StopsEditor } from "../../components/passenger/BookingFormHelpers";
import { Button } from "../../components/ui/button";

const PAYMENT_OPTIONS = [
  { value: "cash", label: "Cash (driver collects)" },
  { value: "card", label: "Card" },
  { value: "account", label: "Account (corporate)" },
];

export const NewBookingModal = ({ onClose, onCreate, initial }) => {
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [pickup, setPickup] = useState(initial?.pickup || null);   // { name, lat, lng }
  const [dropoff, setDropoff] = useState(initial?.dropoff || null);
  const [stops, setStops] = useState([]);        // [{ name, lat, lng } | null]
  const [payment, setPayment] = useState(initial?.payment || "cash");
  const [passengerCount, setPassengerCount] = useState(initial?.passenger_count || 1);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [rideTime, setRideTime] = useState(initial?.scheduled ? "schedule" : "asap");   // "asap" | "schedule"
  const [date, setDate] = useState(initial?.date || "");
  const [time, setTime] = useState(initial?.time || "");
  const [submitting, setSubmitting] = useState(false);
  const fromAi = !!initial;

  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  // Compose the operator's LOCAL date+time into a UTC ISO instant for the
  // backend (which shares the passenger scheduling contract: ≥15 min ahead,
  // ≤30 days out).
  const scheduledIso = useMemo(() => {
    if (rideTime !== "schedule" || !date || !time) return null;
    const dt = new Date(`${date}T${time}`);
    return isNaN(dt.getTime()) ? null : dt.toISOString();
  }, [rideTime, date, time]);

  const scheduleTooSoon = rideTime === "schedule" && scheduledIso &&
    (new Date(scheduledIso).getTime() - Date.now()) < 15 * 60 * 1000;
  const scheduleValid = rideTime === "asap" || (!!scheduledIso && !scheduleTooSoon);

  const canSubmit =
    name.trim().length > 0 &&
    phone.replace(/\D/g, "").length >= 7 &&
    pickup?.lat && dropoff?.lat &&
    scheduleValid &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onCreate({
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        pickup_address: pickup.name,
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        dropoff_address: dropoff.name,
        dropoff_lat: dropoff.lat,
        dropoff_lng: dropoff.lng,
        stops: stops
          .filter((s) => s && s.lat != null && s.lng != null)
          .map((s) => ({ address: s.name, lat: s.lat, lng: s.lng })),
        payment_method: payment,
        passenger_count: passengerCount,
        driver_notes: notes.trim() || null,
        scheduled_for: scheduledIso,   // null → immediate ("ASAP")
      });
      onClose();
    } catch (e) {
      // toast handled by caller; keep modal open so the operator can retry
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="new-booking-modal" onMouseDown={onClose}>
      <div
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-white">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
              <Phone className="w-4 h-4" /> New phone booking
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Book a ride for a customer who called in.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="new-booking-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {fromAi && (
            <div className="flex items-start gap-2 text-xs bg-[#F0F4FF] border border-[#002FA7]/20 text-[#002FA7] rounded-lg px-3 py-2.5" data-testid="booking-ai-prefill-hint">
              <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Pre-filled by the AI Booking assistant. Please <strong>confirm the pickup &amp; destination</strong> from the address suggestions, then review before dispatching.</span>
            </div>
          )}
          <div>
            <label className="label-mini text-muted-foreground">Customer name</label>
            <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
              <User className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jane Doe"
                className="flex-1 outline-none bg-transparent text-sm"
                data-testid="booking-name-input"
              />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Phone number</label>
            <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
              <Phone className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 07700 900123"
                className="flex-1 outline-none bg-transparent text-sm"
                data-testid="booking-phone-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">If this number already exists, the booking links to that customer.</p>
          </div>

          <div>
            <label className="label-mini text-muted-foreground flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Pickup
            </label>
            <div className="border border-border mt-1">
              <PlaceAutocomplete
                value={pickup}
                onChange={setPickup}
                placeholder="Pickup address…"
                testId="booking-pickup"
                seedSearch={fromAi}
                icon={<MapPin className="w-4 h-4 text-[#10B981] mr-2" />}
              />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" /> Destination
            </label>
            <div className="border border-border mt-1">
              <PlaceAutocomplete
                value={dropoff}
                onChange={setDropoff}
                placeholder="Destination address…"
                testId="booking-dropoff"
                seedSearch={fromAi}
                icon={<MapPin className="w-4 h-4 text-[#EF4444] mr-2" />}
              />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Extra stops (optional)</label>
            <div className="border border-border mt-1">
              <StopsEditor stops={stops} onChange={setStops} max={3} />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Payment method</label>
            <select
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-border bg-white text-sm outline-none"
              data-testid="booking-payment-select"
            >
              {PAYMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Passengers</label>
            <select
              value={passengerCount}
              onChange={(e) => setPassengerCount(Number(e.target.value))}
              className="w-full mt-1 px-3 py-2 border border-border bg-white text-sm outline-none"
              data-testid="booking-passenger-count-select"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>{n} passenger{n > 1 ? "s" : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Notes for driver (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. wheelchair accessible, meet at main entrance, flight BA123"
              className="w-full mt-1 px-3 py-2 border border-border bg-white text-sm outline-none resize-none focus:border-[#002FA7]"
              data-testid="booking-notes-input"
            />
          </div>

          <div data-testid="booking-ride-time">
            <label className="label-mini text-muted-foreground flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Ride time
            </label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                onClick={() => setRideTime("asap")}
                className={`px-3 py-2 border text-sm font-semibold transition-colors ${rideTime === "asap" ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-foreground hover:border-[#002FA7]"}`}
                data-testid="booking-ride-time-asap"
              >
                ASAP
              </button>
              <button
                type="button"
                onClick={() => setRideTime("schedule")}
                className={`px-3 py-2 border text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${rideTime === "schedule" ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-foreground hover:border-[#002FA7]"}`}
                data-testid="booking-ride-time-schedule"
              >
                <CalendarClock className="w-4 h-4" /> Schedule for later
              </button>
            </div>

            {rideTime === "schedule" && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="booking-schedule-fields">
                <div>
                  <label className="text-[11px] text-muted-foreground">Pickup date</label>
                  <input
                    type="date"
                    value={date}
                    min={todayStr}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full mt-0.5 px-3 py-2 border border-border bg-white text-sm outline-none focus:border-[#002FA7]"
                    data-testid="booking-schedule-date"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground">Pickup time</label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full mt-0.5 px-3 py-2 border border-border bg-white text-sm outline-none focus:border-[#002FA7]"
                    data-testid="booking-schedule-time"
                  />
                </div>
                {scheduleTooSoon && (
                  <p className="col-span-2 text-[11px] text-[#B91C1C]" data-testid="booking-schedule-warning">
                    Pickup time must be at least 15 minutes from now (and within 30 days).
                  </p>
                )}
                {scheduledIso && !scheduleTooSoon && (
                  <p className="col-span-2 text-[11px] text-muted-foreground" data-testid="booking-schedule-hint">
                    This ride will be held and auto-dispatched near the pickup time.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="booking-cancel-btn">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-[#002FA7] hover:bg-[#001f6e] text-white disabled:opacity-50"
            data-testid="booking-submit-btn"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Booking…</>
              : rideTime === "schedule" ? "Schedule booking" : "Book & dispatch"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewBookingModal;
